import React, { useEffect, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { productionTimeAPI, ProductionState } from '../services/api';
import { showCustomAlert } from './CustomAlert';

const modeOptions = [
  { label: 'Redovan rad', value: 'primary' },
  { label: 'Oduzimanje', value: 'deduction' }
];

const ProductionStates: React.FC = () => {
  const [states, setStates] = useState<ProductionState[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingState, setEditingState] = useState<ProductionState | null>(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    color: '#6366f1',
    icon: 'pi pi-pause-circle',
    order: 0,
    mode: 'deduction' as 'primary' | 'deduction',
    isActive: true
  });

  useEffect(() => {
    void loadStates();
  }, []);

  const loadStates = async () => {
    setLoading(true);
    try {
      const data = await productionTimeAPI.getStates();
      setStates(data);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Error loading production states:', err);
      showCustomAlert.error('Greška pri učitavanju stanja proizvodnje');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingState(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      color: '#6366f1',
      icon: 'pi pi-pause-circle',
      order: states.length + 1,
      mode: 'deduction',
      isActive: true
    });
    setError('');
    setModalVisible(true);
  };

  const openEditModal = (state: ProductionState) => {
    setEditingState(state);
    setFormData({
      name: state.name,
      code: state.code || '',
      description: state.description || '',
      color: state.color || '#6366f1',
      icon: state.icon || 'pi pi-pause-circle',
      order: state.order || 0,
      mode: state.isPrimaryMode ? 'primary' : 'deduction',
      isActive: state.isActive
    });
    setError('');
    setModalVisible(true);
  };

  const handleDelete = (state: ProductionState) => {
    confirmDialog({
      message: `Da li ste sigurni da želite da obrišete stanje "${state.name}"?`,
      header: 'Potvrda brisanja',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await productionTimeAPI.deleteState(state._id);
          await loadStates();
        } catch (err: any) {
          // eslint-disable-next-line no-console
          console.error('Error deleting state:', err);
          showCustomAlert.error('Greška pri brisanju stanja');
        }
      }
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Naziv je obavezan');
      return;
    }

    const payload: Partial<ProductionState> = {
      name: formData.name.trim(),
      code: formData.code ? formData.code.toUpperCase() : undefined,
      description: formData.description || undefined,
      color: formData.color,
      icon: formData.icon || 'pi pi-pause-circle',
      order: formData.order,
      isPrimaryMode: formData.mode === 'primary',
      deductsFromPrimary: formData.mode === 'deduction',
      isActive: formData.isActive
    };

    try {
      setError('');

      if (editingState) {
        await productionTimeAPI.updateState(editingState._id, payload);
      } else {
        await productionTimeAPI.createState(payload);
      }

      setModalVisible(false);
      await loadStates();
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Error saving state:', err);
      setError(err.response?.data?.error || 'Greška pri čuvanju stanja');
    }
  };

  const modeBodyTemplate = (state: ProductionState) => {
    if (state.isPrimaryMode) {
      return <Tag value="Redovan rad" severity="success" />;
    }
    return <Tag value="Oduzimanje" severity="warning" />;
  };

  const statusBodyTemplate = (state: ProductionState) => {
    return (
      <Tag
        value={state.isActive ? 'Aktivno' : 'Neaktivno'}
        severity={state.isActive ? 'success' : 'danger'}
      />
    );
  };

  const iconBodyTemplate = (state: ProductionState) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: state.color || '#6366f1',
            color: '#ffffff'
          }}
        >
          <i className={state.icon || 'pi pi-pause-circle'} />
        </span>
      </div>
    );
  };

  const actionsBodyTemplate = (state: ProductionState) => {
    return (
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Button
          icon="pi pi-pencil"
          className="p-button-text"
          onClick={() => openEditModal(state)}
          tooltip="Izmeni"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-danger"
          onClick={() => handleDelete(state)}
          tooltip="Obriši"
        />
      </div>
    );
  };

  return (
    <div style={{ padding: '2rem' }}>
      <ConfirmDialog />

      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, marginBottom: '1rem' }}>Stanja proizvodnje</h1>
        <Button
          label="+ Dodaj stanje"
          icon="pi pi-plus"
          className="p-button-success"
          onClick={openCreateModal}
        />
      </div>

      <Card>
        <DataTable value={states} loading={loading} paginator rows={10} emptyMessage="Nema stanja">
          <Column header="Ikona" body={iconBodyTemplate} style={{ width: '120px' }} />
          <Column field="name" header="Naziv" sortable />
          <Column field="order" header="Redosled" sortable />
          <Column header="Tip moda" body={modeBodyTemplate} />
          <Column header="Status" body={statusBodyTemplate} />
          <Column header="Akcije" body={actionsBodyTemplate} style={{ width: '150px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={editingState ? 'Izmeni stanje' : 'Dodaj stanje'}
        visible={modalVisible}
        style={{ width: '500px' }}
        onHide={() => setModalVisible(false)}
        footer={
          <div>
            <Button
              label="Otkaži"
              icon="pi pi-times"
              onClick={() => setModalVisible(false)}
              className="p-button-text"
            />
            <Button
              label={editingState ? 'Sačuvaj' : 'Kreiraj'}
              icon="pi pi-check"
              onClick={handleSubmit}
            />
          </div>
        }
      >
        {error && <Message severity="error" text={error} style={{ marginBottom: '1rem' }} />}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label htmlFor="name" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Naziv <span style={{ color: 'red' }}>*</span>
            </label>
            <InputText
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label htmlFor="code" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Šifra (opciono)
            </label>
            <InputText
              id="code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              style={{ width: '100%' }}
              placeholder="npr. NORMAL, CS, BM"
            />
          </div>

          <div>
            <label htmlFor="description" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Opis (opciono)
            </label>
            <InputText
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label htmlFor="color" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Boja
            </label>
            <InputText
              id="color"
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              style={{ width: '100%' }}
              placeholder="#6366f1"
            />
          </div>

          <div>
            <label htmlFor="icon" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Ikona (PrimeIcons klasa)
            </label>
            <InputText
              id="icon"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              style={{ width: '100%' }}
              placeholder="pi pi-pause-circle"
            />
          </div>

          <div>
            <label htmlFor="order" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Redosled
            </label>
            <InputNumber
              id="order"
              value={formData.order}
              onValueChange={(e) => setFormData({ ...formData, order: e.value || 0 })}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Tip moda</label>
            <Dropdown
              value={formData.mode}
              options={modeOptions}
              onChange={(e) => setFormData({ ...formData, mode: e.value })}
              appendTo="self"
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Checkbox
              inputId="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.checked || false })}
            />
            <label htmlFor="isActive" style={{ cursor: 'pointer' }}>
              Aktivno
            </label>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ProductionStates;